/**
 * 优化的 OAuth 工具类
 * 使用弹出窗口实现的 OAuth 管理器
 */

// 事件发射器类
class EventEmitter<T = any> {
  private emitter = new EventTarget();

  get event() {
    return (callback: (data: T) => void) => {
      const handler = (event: Event) => {
        callback((event as CustomEvent<T>).detail);
      };
      this.emitter.addEventListener('event', handler);
      return {
        dispose: () => this.emitter.removeEventListener('event', handler)
      };
    };
  }

  fire(data: T) {
    this.emitter.dispatchEvent(new CustomEvent('event', { detail: data }));
  }
}

// OAuth 提供商枚举
export enum OAuthProvider {
  GitHub = 'github',
  Google = 'google', 
  Vercel = 'vercel',
  Supabase = 'supabase'
}

// OAuth 状态枚举
export enum OAuthStatus {
  SUCCESS = 1,
  USER_CLOSE_THE_WINDOW = 2,
  HTTP_ERROR = 3,
  OAUTH_THIRD_PART = 4
}

// 窗口消息接口
interface WindowMessage {
  status: 'success' | 'error';
  code?: string;
  error?: string;
  state?: string;
  [key: string]: any;
}

// 窗口关闭事件接口
interface WindowCloseEvent {
  source: string;
  type: string;
  window?: Window;
}

// 窗口操作类
class WindowAction {
  private _onWindowMessage = new EventEmitter<WindowMessage>();
  private _onWindowClose = new EventEmitter<WindowCloseEvent>();
  private timer: any = null;
  private windowTimers = new Map<Window, any>();

  get onWindowMessage() {
    return this._onWindowMessage.event;
  }

  get onWindowClose() {
    return this._onWindowClose.event;
  }

  // 停止监听特定窗口的关闭事件
  stopWatchingWindow(window: Window) {
    const timer = this.windowTimers.get(window);
    if (timer) {
      clearInterval(timer);
      this.windowTimers.delete(window);
    }
  }

  private rawMessageHandler = (event: MessageEvent) => {
    // 只处理来自弹窗的消息，不处理页面自己发送的消息
    // 确保消息来源不是当前窗口，并且来源是一个有效的窗口对象
    if (event.origin === window.location.origin && 
        event.source !== window && 
        event.source && 
        typeof event.source === 'object' &&
        'postMessage' in event.source) {
      this._onWindowMessage.fire(event.data);
    }
  };

  private initEvent() {
    window.addEventListener('message', this.rawMessageHandler);
  }

  dispose() {
    window.removeEventListener('message', this.rawMessageHandler);
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    // 清理所有窗口定时器
    this.windowTimers.forEach(timer => clearInterval(timer));
    this.windowTimers.clear();
  }

  openWindow(url: string, windowName: string, type: string): Window | null {
    // 计算窗口居中位置
    const screenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const screenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;
    
    const width = window.innerWidth || document.documentElement.clientWidth || screen.width;
    const height = window.innerHeight || document.documentElement.clientHeight || screen.height;
    
    const systemZoom = width / window.screen.availWidth;
    
    // 窗口配置
    const windowFeatures = [
      'personalbar=0',    // 隐藏个人工具栏
      'toolbar=0',        // 隐藏工具栏  
      'scrollbars=1',     // 允许滚动条
      'resizable=1',      // 允许调整大小
      'width=600',        // 宽度 600px
      'height=600',       // 高度 600px
      `left=${(width - 600) / 2 / systemZoom + screenLeft}`,  // 居中 X
      `top=${(height - 600) / 2 / systemZoom + screenTop}`    // 居中 Y
    ].join(',');

    const popup = window.open(url, windowName, windowFeatures);
    if (popup) {
      popup.focus();
      
      // 为每个窗口创建独立的定时器
      const timer = setInterval(() => {
        if (popup.closed) {
          this._onWindowClose.fire({ source: windowName, type, window: popup });
          clearInterval(timer);
          this.windowTimers.delete(popup);
        }
      }, 100);
      
      this.windowTimers.set(popup, timer);
    }
    
    return popup;
  }

  constructor() {
    if (typeof window !== 'undefined') {
      this.initEvent();
    }
  }
}

// OAuth 管理器类
export class PopupOAuthManager {
  private _onOAuthFail = new EventEmitter<any>();
  private _onOAuthSuccess = new EventEmitter<any>();
  private windowAction = new WindowAction();
  private openedWindows = new Set<Window>();
  private successfulWindows = new Set<Window>();
  private oauthSuccessful = false;

  get onOAuthFail() {
    return this._onOAuthFail.event;
  }

  get onOAuthSuccess() {
    return this._onOAuthSuccess.event;
  }

  openOAuthWindow(provider: OAuthProvider, type: string, options?: { state?: string }) {
    // 重置OAuth成功标志
    this.oauthSuccessful = false;
    
    const { state = '' } = options || {};
    let url = '';

    switch (provider) {
      case OAuthProvider.Vercel:
        // 使用 Vercel 登录页面的重定向方式
        const vercelUrl = new URL('https://vercel.com/login');
        vercelUrl.searchParams.set(
          'next', 
          `https://vercel.com/integrations/codeflicker/new?state=${state}`
        );
        url = vercelUrl.toString();
        break;
        
      case OAuthProvider.GitHub:
        // 如果需要支持其他提供商，可以在这里添加
        break;
        
      default:
        throw new Error(`Unsupported OAuth provider: ${provider}`);
    }

    const popup = this.windowAction.openWindow(url, provider, type);
    if (popup) {
      this.openedWindows.add(popup);
    }
    return popup;
  }

  dispose() {
    this.windowAction.dispose();
  }

  constructor() {
    // 监听窗口消息
    this.windowAction.onWindowMessage((message) => {
      if (message.status === 'success') {
        console.log('OAuth success message received, setting success flag');
        // 立即设置OAuth成功标志
        this.oauthSuccessful = true;
        
        // 标记所有窗口为成功状态
        this.openedWindows.forEach(window => {
          this.successfulWindows.add(window);
        });
        
        this._onOAuthSuccess.fire({ ...message });
      } else {
        this._onOAuthFail.fire({ ...message });
      }
    });

    // 监听窗口关闭事件
    this.windowAction.onWindowClose((event) => {
      // 检查OAuth是否已经成功
      const isSuccessful = this.oauthSuccessful || (event.window && this.successfulWindows.has(event.window));
      
      console.log('Window closed. OAuth successful:', this.oauthSuccessful, 'Is successful:', isSuccessful);
      
      // 清理已关闭的窗口
      if (event.window) {
        this.openedWindows.delete(event.window);
        this.successfulWindows.delete(event.window);
      }
      
      // 只有在OAuth未成功的情况下才触发失败事件
      if (!isSuccessful) {
        console.log('Triggering OAuth fail event');
        this._onOAuthFail.fire({ 
          error: 'user_cancelled',
          message: 'User closed the OAuth window',
          source: event.source,
          type: event.type
        });
      } else {
        console.log('OAuth window closed after successful authentication');
      }
    });
  }
}

// 导出单例实例
export const popupOAuthManager = new PopupOAuthManager();

// 工具函数
export function generateRandomString(length = 32): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return result;
}